import { useEffect, useState } from 'react';
import { Search } from 'lucide-react';
import type { CookDetails, SmokerSpec } from '../../types';

interface Props {
  data: Partial<CookDetails>;
  onChange: (data: Partial<CookDetails>) => void;
  onNext: () => void;
}

export default function SmokerSelect({ data: _data, onChange, onNext }: Props) {
  const [smokers, setSmokers] = useState<SmokerSpec[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Load smokers from config
    fetch('/config/smokers.json')
      .then((res) => res.json())
      .then((data) => {
        setSmokers(data);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Failed to load smokers:', err);
        setLoading(false);
      });
  }, []);

  const filteredSmokers = smokers.filter(
    (s) =>
      s.fullName.toLowerCase().includes(search.toLowerCase()) ||
      s.manufacturer.toLowerCase().includes(search.toLowerCase())
  );

  const handleSelect = (smoker: SmokerSpec) => {
    onChange({ smokerModel: `${smoker.manufacturer.toLowerCase()}-${smoker.model.toLowerCase().replace(/\s+/g, '-')}` });
    setTimeout(onNext, 200);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-flame-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-smoke-400" />
        <input
          type="text"
          placeholder="Search smokers..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="input-field pl-10"
        />
      </div>

      {/* Smoker List */}
      <div className="space-y-2 max-h-[400px] overflow-y-auto">
        {filteredSmokers.map((smoker) => (
          <button
            key={`${smoker.manufacturer}-${smoker.model}`}
            onClick={() => handleSelect(smoker)}
            className="w-full text-left p-4 border-2 border-smoke-200 rounded-lg hover:border-flame-500 hover:bg-flame-50 transition-all"
          >
            <div className="font-semibold text-smoke-900">{smoker.fullName}</div>
            <div className="text-sm text-smoke-600 mt-1">
              {smoker.dimensions.cookingSpaceSquareInches} sq in · {smoker.fuelSystem.fuelType}
            </div>
          </button>
        ))}
      </div>

      {filteredSmokers.length === 0 && (
        <div className="text-center py-12 text-smoke-600">
          No smokers found. Try a different search.
        </div>
      )}
    </div>
  );
}
